"use client";

import { useState, useEffect, useRef, useContext, forwardRef } from "react";
import styled, { css } from "styled-components";
import { accentColorConfirmation, accentColorDanger, accentColorLink, backgroundColorLevelCurrent, borderColorPrimary, borderColorSecondary, highlightBlue, setAppearanceConstantLocal, spacing8, spacing16, spacing24, spacing32, textColorSecondary, textColorTertiary, whiteTransparency80 } from "@churchofjesuschrist/eden-style-constants";
import { Secondary, Icon } from "@churchofjesuschrist/eden-buttons";
import { Delete, AlertFilled, CircleSelectedFilled } from "@churchofjesuschrist/eden-icons";
import Row from "@churchofjesuschrist/eden-row";
import { IdContext, DescribedByContext, ValidityStateContext } from "../utils/context";
import { H5 } from "@churchofjesuschrist/eden-headings";
import { Text4 } from "@churchofjesuschrist/eden-text";
import Stack from "@churchofjesuschrist/eden-stack";
import { valuesByValidityState, formElementDisabledBackgroundDef, formElementDisabledBackground, formElementDisabledBorderDef, formElementDisabledBorder } from "../utils/sharedCss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const fileMatchesAccept = (file, accept) => {
  if (accept) {
    const extension = file.name.split(".").pop();
    const type = file.type;
    const joinedAccept = accept.split(",").map(item => item.trim());
    let isValid = false;
    joinedAccept.forEach(acceptValue => {
      if (acceptValue === `.${extension}`) {
        isValid = true;
      } else if (acceptValue === type) {
        isValid = true;
      } else {
        const splitAcceptValue = acceptValue.split("/");
        if (splitAcceptValue[1] === "*" && type.includes(splitAcceptValue[0])) {
          isValid = true;
        }
      }
    });
    return isValid;
  } else {
    return true;
  }
};
const [backgroundFileUploaderDef, backgroundFileUploader] = setAppearanceConstantLocal("background-file-uploader", {
  light: "#F7F8F8",
  // referenced as backgroundFileUploader in figma
  dark: whiteTransparency80,
  prefix: "eden"
});
const FileNameContainer = styled(Text4).withConfig({
  componentId: "sc-15by3kn-0"
})(["width:100%;text-overflow:ellipsis;overflow:hidden;white-space:nowrap;"]);
const FileInfoContainer = styled(Row).withConfig({
  componentId: "sc-15by3kn-1"
})(["padding:", ";border:1px solid ", ";width:17.5rem;background:", ";justify-self:", ";flex-wrap:nowrap;"], spacing8, ({
  $validityState
}) => valuesByValidityState[$validityState]?.borderColor ?? borderColorPrimary, backgroundColorLevelCurrent, ({
  $isDroppable
}) => $isDroppable ? "center" : "start");
const HiddenInput = styled.input.withConfig({
  componentId: "sc-15by3kn-2"
})(["position:absolute;opacity:0;width:0;height:0;"]);
export const InternalUseOnlyFileInput = styled.div.withConfig({
  componentId: "sc-15by3kn-3"
})(["position:relative;", ";", ";", ";", ";", ";"], formElementDisabledBackgroundDef, formElementDisabledBorderDef, backgroundFileUploaderDef, ({
  $isDroppable,
  $validityState
}) => $isDroppable ? css(["padding:", " ", ";border:2px dashed ", ";background:", ";&[data-is-drag-active]{border-color:", ";background:", ";& > :is(div,input){pointer-events:none;}}"], spacing32, spacing16, valuesByValidityState[$validityState]?.borderColor ?? borderColorSecondary, backgroundFileUploader, accentColorLink, highlightBlue) : "", ({
  $disabled,
  $isDroppable
}) => $disabled && $isDroppable ? `
      cursor: not-allowed;
      background: ${formElementDisabledBackground()};
      border-color: ${formElementDisabledBorder()};
  ` : "");
const StyledH5 = styled(H5).withConfig({
  componentId: "sc-15by3kn-4"
})(["color:", ";justify-self:center;"], ({
  $disabled
}) => $disabled ? textColorTertiary : textColorSecondary);
const ChooseFileButton = styled(Secondary).withConfig({
  componentId: "sc-15by3kn-5"
})(["justify-self:", ";"], ({
  $isDroppable
}) => $isDroppable ? "center" : "start");

/**
 * @typedef {object} File
 * @property {number} lastModified - The last modified date of the file
 * @property {string} name - The name of the file
 * @property {number} size - The size of the file
 * @property {string} type - The MIME type of the file
 */

/**
 * The `FileInput` allows the user to choose one or more files from their device's storage.
 * @param {object} props - The component props
 * @param {string} [props.accept] - One or more unique file type specifiers describing file types to allow [docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#Unique_file_type_specifiers)
 * @param {string} [props.aria-describedby] - Identifies the element (or elements) that describes the FileInput
 * @param {"user" | "environment" | ""} [props.capture] - What source to use for capturing image or video data
 * @param {React.ReactNode} [props.children] - Content to render inside the FileInput Button Component
 * @param {File | File[]} [props.defaultValue] - Default value for uncontrolled use that is an instance of [File](https://developer.mozilla.org/en-US/docs/Web/API/File). If `multiple` is being used, this must be an array of [File](https://developer.mozilla.org/en-US/docs/Web/API/File) objects.
 * @param {boolean} [props.disabled] - Makes the input not mutable, focusable, or even submitted with the form
 * @param {string} [props.id] - An identifier which must be unique in the whole document
 * @param {boolean} [props.isDroppable] - Enables drag and drop capabilities
 * @param {boolean} [props.multiple] - Enables multiple file upload capabilities
 * @param {string} [props.name] - Used to reference the FileInput in JavaScript, or to reference form data after a form is submitted
 * @param {Function} [props.onChange] - Callback to execute when the value changes
 * @param {boolean} [props.required] - Whether the FileInput needs at least one file to be considered valid
 * @param {File | File[]} [props.value] - A value for controlled use that is an instance of [File](https://developer.mozilla.org/en-US/docs/Web/API/File). If `multiple` is being used, this must be an array of [File](https://developer.mozilla.org/en-US/docs/Web/API/File) objects.
 * @param {string} [props.invalidFileLabel] - Label for the invalid file indicator
 * @param {string} [props.validFileLabel] - Label for the valid file indicator
 * @param {string} [props.removeFileLabel] - Label for the remove file button
 * @param {string} [props.dragFilesLabel] - Label for the `Drag file/s here or` heading
 * @param {React.Ref} forwardedRef - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The FileInput component
 */
const FileInputComponent = ({
  accept,
  "aria-describedby": ariaDescribedBy,
  capture,
  children = "Browse",
  defaultValue,
  disabled,
  id,
  isDroppable,
  multiple,
  name,
  onChange,
  required,
  value,
  invalidFileLabel = "Invalid file",
  validFileLabel = "Valid file",
  removeFileLabel = "Remove file",
  dragFilesLabel = `Drag file${multiple ? "s" : ""} here or`,
  ...props
}, forwardedRef) => {
  const [innerValue, setInnerValue] = useState(value ?? defaultValue);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const chooseFileButton = useRef();
  const dropZone = useRef();
  const ignoreOnChange = useRef(innerValue);
  const idContext = useContext(IdContext);
  const describedByContext = useContext(DescribedByContext);
  const validityStateContext = useContext(ValidityStateContext);
  const internalRef = useRef();
  const ref = forwardedRef ?? internalRef;
  const setValue = () => {
    if (ref.current) {
      const dataTransfer = new DataTransfer();
      if (multiple && innerValue) {
        innerValue.forEach(file => {
          dataTransfer.items.add(file);
        });
      } else if (innerValue) {
        dataTransfer.items.add(innerValue);
      }
      ref.current.files = dataTransfer.files;
    }
  };
  useEffect(() => {
    ignoreOnChange.current = value ?? defaultValue;
    setInnerValue(ignoreOnChange.current);
  }, [value, defaultValue]);
  useEffect(() => {
    setValue();
    if (!Object.is(ignoreOnChange.current, innerValue)) {
      onChange?.({
        target: {
          value: innerValue,
          name: name
        }
      });
      ref.current.dispatchEvent(new Event("validate", {
        bubbles: true
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerValue?.length, innerValue?.name]);
  useEffect(() => {
    const closestForm = ref.current.form;
    if (!closestForm) {
      return;
    }
    const resetHandler = () => {
      ignoreOnChange.current = value ?? defaultValue;
      setInnerValue(ignoreOnChange.current);
      setInvalidFiles([]);
    };
    closestForm.addEventListener("reset", resetHandler);
    return () => closestForm.removeEventListener("reset", resetHandler);
  });
  useEffect(() => {
    ref.current._edenOnValidateDetails = {
      setInvalidFiles,
      fileMatchesAccept
    };
  }, [ref]);
  const handleInputSelect = () => {
    ref.current.click();
  };
  const removeFromValue = (fileName, event) => {
    event.preventDefault();
    event.stopPropagation();
    const removeFromClone = prevValue => {
      const clone = prevValue ? [...prevValue] : [];
      const index = clone.findIndex(({
        name
      }) => name === fileName);
      clone.splice(index, 1);
      if (clone?.length === 0) {
        ref.current.value = "";
      }
      return clone;
    };
    if (multiple) {
      setInvalidFiles(removeFromClone);
      setInnerValue(removeFromClone);
    } else {
      if (!ignoreOnChange.current) {
        ignoreOnChange.current = null;
      }
      setInvalidFiles([]);
      setInnerValue(undefined);
    }
  };
  const changeHandler = event => {
    const files = [...event.target.files];
    setInnerValue(prevInnerValue => {
      const filteredFiles = multiple && prevInnerValue ? files.filter(file => !prevInnerValue.find(elem => elem.name === file.name)) : files;
      if (filteredFiles.length === 0) {
        setValue();
        return prevInnerValue;
      }
      let newValue;
      if (multiple) {
        newValue = prevInnerValue ? [...prevInnerValue, ...filteredFiles] : [...filteredFiles];
      } else {
        newValue = filteredFiles[0];
      }
      return newValue;
    });
  };
  const handleDrop = event => {
    event.preventDefault();
    delete dropZone.current.dataset.isDragActive;
    ref.current.files = event.dataTransfer.files;
    changeHandler({
      target: ref.current
    });
  };
  const handleDragEnter = event => {
    event.preventDefault();
    dropZone.current.dataset.isDragActive = "";
  };
  const handleDragOver = event => event.preventDefault();
  const handleDragLeave = () => {
    delete dropZone.current.dataset.isDragActive;
  };
  return /*#__PURE__*/_jsxs(InternalUseOnlyFileInput, {
    ref: dropZone,
    $disabled: disabled,
    $validityState: validityStateContext,
    $isDroppable: isDroppable,
    ...(isDroppable && !disabled ? {
      onDrop: handleDrop,
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave
    } : {}),
    ...props,
    children: [/*#__PURE__*/_jsxs(Stack, {
      gapSize: "8",
      children: [isDroppable && dragFilesLabel ? /*#__PURE__*/_jsx(StyledH5, {
        renderAs: "div",
        $disabled: disabled,
        children: dragFilesLabel
      }) : null, /*#__PURE__*/_jsx(ChooseFileButton, {
        ref: chooseFileButton,
        disabled: disabled,
        onClick: handleInputSelect,
        $isDroppable: isDroppable,
        children: children
      }), multiple && innerValue?.length || !multiple && innerValue ? (multiple ? innerValue : [innerValue]).map(({
        name
      }) => /*#__PURE__*/_jsxs(FileInfoContainer, {
        $isDroppable: isDroppable,
        verticalAlign: "center",
        gapSize: "4",
        $validityState: invalidFiles.find(file => file.name === name) ? "error" : "success",
        children: [invalidFiles.find(file => file.name === name) ? /*#__PURE__*/_jsx(AlertFilled, {
          title: invalidFileLabel,
          size: spacing24,
          color: accentColorDanger
        }) : /*#__PURE__*/_jsx(CircleSelectedFilled, {
          title: validFileLabel,
          size: spacing24,
          color: accentColorConfirmation
        }), /*#__PURE__*/_jsx(FileNameContainer, {
          children: name
        }), /*#__PURE__*/_jsx(Icon, {
          title: removeFileLabel,
          onClick: event => removeFromValue(name, event),
          size: "small",
          children: /*#__PURE__*/_jsx(Delete, {
            size: spacing24
          })
        })]
      }, name)) : null]
    }), /*#__PURE__*/_jsx(HiddenInput, {
      accept: accept,
      "aria-describedby": ariaDescribedBy ? ariaDescribedBy : describedByContext,
      capture: capture,
      disabled: disabled,
      id: id ? id : idContext,
      multiple: multiple,
      name: name,
      onChange: event => {
        event.stopPropagation();
        changeHandler(event);
      },
      onFocus: () => chooseFileButton.current.focus(),
      ref: ref,
      required: required,
      tabIndex: "-1",
      type: "file"
    })]
  });
};
const FileInput = /*#__PURE__*/forwardRef(FileInputComponent);
FileInput.displayName = "FileInput";

// Custom property for the FormField to consume
FileInput.isInput = true;
export default FileInput;